import type { ClienteRef, DateOnlyString } from '../common';

/** Linea congelada de `cobro_cliente_detalle` (Fase C/E2). */
export interface CobroLinea {
  idServicio: number;
  descripcion: string;
  precioUnitario: string;
  subtotal: string;
}

/**
 * Values persisted in `cobro_cliente.generado_por`: MANUAL, CRON, and ADELANTO.
 * `REEMISION` marks invoices produced by the atomic re-issue composition; it is
 * a TS-only audit value — `cobro_cliente.generado_por` has no DB CHECK
 * constraint, so no DDL accompanies it.
 */
export const COBRO_GENERADO_POR = {
  MANUAL: 'MANUAL',
  CRON: 'CRON',
  ADELANTO: 'ADELANTO',
  REEMISION: 'REEMISION',
} as const;

export type CobroGeneradoPor = (typeof COBRO_GENERADO_POR)[keyof typeof COBRO_GENERADO_POR];

/** Origin of a persisted invoice-generation execution. */
export const COBRO_GENERACION_ORIGEN = {
  CRON: 'CRON',
  MANUAL: 'MANUAL',
} as const;

export type CobroGeneracionOrigen =
  (typeof COBRO_GENERACION_ORIGEN)[keyof typeof COBRO_GENERACION_ORIGEN];

/** Terminal and in-flight states for an invoice-generation execution. */
export const COBRO_GENERACION_ESTADO = {
  EN_PROGRESO: 'EN_PROGRESO',
  COMPLETADA: 'COMPLETADA',
  COMPLETADA_CON_ERRORES: 'COMPLETADA_CON_ERRORES',
  FALLIDA: 'FALLIDA',
} as const;

export type CobroGeneracionEstado =
  (typeof COBRO_GENERACION_ESTADO)[keyof typeof COBRO_GENERACION_ESTADO];

/** Stable, safe error codes exposed by generation endpoints. */
export const COBRO_GENERACION_ERROR_CODE = {
  EN_PROGRESO: 'COBRO_GENERACION_EN_PROGRESO',
  GENERACION_FALLIDA: 'COBRO_GENERACION_FALLIDA',
} as const;

export type CobroGeneracionErrorCode =
  (typeof COBRO_GENERACION_ERROR_CODE)[keyof typeof COBRO_GENERACION_ERROR_CODE];

/** Business discriminator for invoices. */
export const INVOICE_TYPE = {
  PERIODICA: 'PERIODICA',
  EXTRAORDINARIA: 'EXTRAORDINARIA',
} as const;

/** Spanish alias kept for consumers that use the persisted domain vocabulary. */
export const TIPO_FACTURA = INVOICE_TYPE;

export type InvoiceType = (typeof INVOICE_TYPE)[keyof typeof INVOICE_TYPE];

/** Estado de una `pago_aplicacion` (spec: payment-application-ledger). */
export const APLICACION_ESTADO = {
  ACTIVA: 'ACTIVA',
  REVERSADA: 'REVERSADA',
} as const;

export type AplicacionEstado = (typeof APLICACION_ESTADO)[keyof typeof APLICACION_ESTADO];

export interface CobroStatsResponse {
  total: number;
  emitido: string;
  pendientes: number;
  pagadas: number;
  anuladas: number;
  promedio: string;
}

/**
 * Two independent, non-negative balance values per spec
 * (Domain: client-balance-reporting). NEVER netted together.
 */
export interface ClientBalance {
  deudaPendiente: string;
  saldoAFavor: string;
}

/**
 * Per-invoice balance rollup, derived from `view_invoice_balance`.
 * `saldoPendiente` is clamped to zero (non-negative).
 */
export interface InvoiceBalance {
  gross: string;
  ajustesAplicados: string;
  montoAplicado: string;
  saldoPendiente: string;
}

/**
 * A single row from `pago_aplicacion`. Exactly one of `idPago` or `idCredito`
 * is set per the design (one append-only ledger with explicit credit lots).
 */
export interface BillingApplication {
  id: number;
  idFactura: number;
  idPago: number | null;
  idCredito: number | null;
  montoAplicado: string;
  estado: AplicacionEstado;
}

/**
 * A single row from `billing_event`. `metadata` is free-form JSONB for
 * forensic context (operation-specific data, source references, etc.).
 */
export interface BillingEvent {
  id: number;
  idCliente: number;
  operacion: string;
  referenciaId: number | null;
  actor: string | null;
  motivo: string | null;
  metadata: Record<string, unknown>;
}

/** Scopes allowed for `cobro_excepcion` (spec: cobro_excepcion is restricted). */
export const AJUSTE_SCOPE_TIPO = {
  INVOICE: 'INVOICE',
  PERIOD: 'PERIOD',
} as const;

export type AjusteScopeTipo = (typeof AJUSTE_SCOPE_TIPO)[keyof typeof AJUSTE_SCOPE_TIPO];

export interface CobroClienteResponse {
  id: number;
  idCliente: number;
  idEstado: number;
  tipoFactura: InvoiceType;
  /** Date-only (period DATE columns) — see DateOnlyString contract. */
  fechaInicio: DateOnlyString | null;
  fechaFin: DateOnlyString | null;
  concepto: string | null;
  valor: string;
  fechaCreacion: Date;
  fechaModificacion: Date | null;
  comentario: string | null;
  motivoAnulacion: string | null;
  fechaAnulacion: Date | null;
  usuarioModifica: string | null;
  /** Date-only (fecha_cobro DATE column) — see DateOnlyString contract. */
  fechaCobro: DateOnlyString;
  /** Date-only persisted due date; legacy rows retain the historical +15-day derivation. */
  fechaVencimiento?: DateOnlyString | null;
  /** Whether this invoice was allowed to consume available client credit. */
  aplicarSaldoAFavor: boolean;
  cliente: ClienteRef | null;
  /**
   * Resolved catalogo ESTADO_COBRO reference ({ key, value }). List and detail
   * endpoints resolve it so consumers do not infer PAGADA/VIGENTE from
   * annulment metadata.
   */
  estado?: { key: string; value: string } | null;
  /**
   * Lineas congeladas de `cobro_cliente_detalle`. Solo el detalle
   * (GET /admin/cobros/:id) lo trae. Facturas historicas (sin lineas) lo
   * omiten; el frontend muestra una linea resumen con el valor.
   */
  lineas?: CobroLinea[];
  /**
   * Conteo de lineas congeladas, presente solo en el listado por cliente
   * (cliente 360) para mostrar cuantas lineas tiene cada factura.
   */
  lineasCount?: number;
  /**
   * M10: true cuando la factura es "del sistema" — tiene lineas congeladas
   * o fue emitida por el generador automático (CRON). Las facturas de
   * prepago (ADELANTO) se representan con líneas congeladas. false = factura
   * "histórica" ingresada a mano antes del generador v2. Reemplaza el
   * chequeo de strings mágicos (generadoPor === 'CRON' / comentario).
   */
  tieneLineas: boolean;
  /**
   * Invoice origin audit value: 'CRON' (automatic generator), 'ADELANTO'
   * (prepayment flow), or 'MANUAL'. It allows Cliente 360 to distinguish
   * "Sin desglose" (0 líneas, generated by CRON) from "Histórica" (0 líneas,
   * entered manually before generator v2).
   */
  generadoPor?: CobroGeneradoPor | null;
  /**
   * Per-invoice balance rollup (migration 0033 view_invoice_balance).
   * The cliente 360 cobros contract hydrates this value; cheaper global lists
   * may omit it and detail responses return it when the view row exists.
   */
  balance?: InvoiceBalance | null;
  /**
   * Applications against this invoice (only on detail endpoints).
   */
  aplicaciones?: BillingApplication[];
  /**
   * Re-issue gate input (spec IR-W1 FE): true when an active `prepago_periodo`
   * (package not CANCELADO) links to this invoice. Lists/detail omit it when
   * the batch lookup is not run, so `canReissue` treats missing as `false`.
   */
  linkedToPrepaid?: boolean;
  /**
   * Denormalized client billing day (`cliente.dia_cobro`) for the re-issue
   * window gate. `null` when the client has no billing day or the join missed.
   */
  diaCobro?: number | null;
}

export interface GenerarCobrosResult {
  generated: number;
  /** Periodos que ya existían (idempotencia) y se omitieron en esta corrida. */
  skipped?: number;
  /** Clients whose per-client transaction was rolled back after an error. */
  failed?: number;
  /** Aggregate credit consumed at generation (string Q0 when none). */
  creditoAplicado?: string;
  /** Persisted execution identifier when history recording is available. */
  executionId?: number;
}

export interface CobroGeneracionCounters {
  generated: number;
  skipped: number;
  failed: number;
  creditoAplicado: string;
}

export interface CobroGeneracionSummary {
  id: number;
  origen: CobroGeneracionOrigen;
  estado: CobroGeneracionEstado;
  fechaNegocio: DateOnlyString;
  catchUp: boolean;
  actor: string;
  actorId: string | null;
  counters: CobroGeneracionCounters;
  startedAt: Date;
  finishedAt: Date | null;
  durationMs: number | null;
  errorCode: string | null;
}

export interface CobroGeneracionDetail extends CobroGeneracionSummary {
  metadata: Record<string, unknown>;
}

export interface CobroGeneracionScheduleContext {
  expression: string;
  timeZone: string;
  businessDate: DateOnlyString;
  nextScheduledAt: Date | null;
  nextScheduleAvailable: boolean;
  activeExecution: CobroGeneracionSummary | null;
  lastExecution: CobroGeneracionSummary | null;
}

/**
 * Prepaid package (coverage schedule) — materialises into monthly invoices.
 * fechaInicio/fechaFin are date-only (DATE columns); fechaAdquisicion and
 * fechaCancelacion are real instants (timestamptz).
 */
export interface PrepagoPaquete {
  id: number;
  idCliente: number;
  bruto: string;
  descuentoManual: string;
  neto: string;
  mesesAdelantado: number;
  fechaInicio: DateOnlyString;
  fechaFin: DateOnlyString;
  fechaAdquisicion: Date;
  fechaCancelacion: Date | null;
  estado: 'ACTIVO' | 'CANCELADO' | 'CONSUMIDO';
}

export interface PrepagoPeriodoLinea {
  idPeriodo: number;
  idServicio: number;
  idPlan: number;
  descripcion: string;
  precioUnitario: string;
  subtotal: string;
}

export interface PrepagoPeriodo {
  id: number;
  idPaquete: number;
  /** Date-only (periodo DATE columns) — see DateOnlyString contract. */
  periodoInicio: DateOnlyString;
  periodoFin: DateOnlyString;
  idCobro: number | null;
  materializado: boolean;
}

export interface PrepagoContrato extends PrepagoPaquete {
  periodos: PrepagoPeriodo[];
  lineas: PrepagoPeriodoLinea[];
}
